/**
 * SCORPIO Configurator — Cattelan Italia tables (SKORPIO range) + chairs (BELINDA / BELINDA ML).
 *
 * Architecture: hybrid project + items.
 *   project = { name, client, date, status, ... , items: Item[] }
 *   item    = { id, kind: 'table' | 'chair', config }
 *
 * A single project (e.g. a dining-room order) holds multiple tables AND/OR chairs.
 *
 * Currency: EUR (trade prices from catalog/Cattelan_general_pricelist_EUR.pdf).
 * Verified by: `node validate-prices.mjs` → 286/286 pass.
 */

import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import * as THREE from 'three';
import * as XLSX from 'xlsx';
import {
  Plus, Trash2, Table as TableIcon, Armchair, FileDown, Printer,
  ChevronDown, ChevronUp, Search, Save, FolderOpen, Sparkles,
} from 'lucide-react';

import {
  // tables
  COLLECTIONS, COLLECTION_BY_CODE, BASE_FINISHES, BASE_BY_CODE,
  GLASS_TOPS, CRYSTALART_TOPS, CERAMIC_TOPS, WOOD_TOPS, PROFILE_FINISHES,
  CERAMIC_AVAILABILITY, SIZES_BY_COLLECTION,
  availableBases, availableTops, availableInsertsOrProfiles,
  PRICE, lookupPrice, buildSku,
  // chairs
  CHAIR_COLLECTIONS, CHAIR_FRAMES, CHAIR_SIZES,
  CHAIR_FABRICS, CHAIR_MICRO_NUBUCK, CHAIR_SOFT_LEATHER,
  CHAIR_SOFT_LEATHER_GLOVE, CHAIR_SYNTHETIC_LEATHER, CHAIR_CUSTOM,
  CHAIR_UPHOLSTERY_GROUPS, CHAIR_UPHOLSTERY_GROUP_LABEL,
  chairUpholsteryGroup, lookupChairPrice, buildChairSku,
  // photos
  PHOTO_TEXTURE_MANIFEST, photoTextureUrl,
} from './data/catalog.js';

import { buildTableMesh, buildChairMesh } from './three/archetypes.js';
import { t, LANGS } from './i18n.js';
import AICopilot from './ai/copilot.jsx';
import RenderModal from './ai/render-modal.jsx';
import { buildRenderPrompt } from './ai/prompt-builder.js';

// ═══════════════════════════════════════════════════════════════════════════
//  HELPERS
// ═══════════════════════════════════════════════════════════════════════════

let _itemIdCounter = 1;
const nextItemId = () => _itemIdCounter++;

const EUR = new Intl.NumberFormat('en-IE', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 });
const fmtEUR = (n) => (n == null ? '— (Price on request)' : EUR.format(n));

function makeTableItem(args = {}) {
  const collection = args.collection || 'CL';
  const sizes = SIZES_BY_COLLECTION[collection] || [];
  const firstSize = sizes[0];
  if (!firstSize) return null;
  const sizeKey = args.size_key || args.sizeKey || firstSize.key;
  const matchedSize = sizes.find(s => s.key === sizeKey) || firstSize;
  const topCode = args.top_code || args.topCode || matchedSize.tops?.[0];
  let insertOrProfile = args.insert_or_profile || args.insertOrProfile;
  if (!insertOrProfile) {
    if (collection === 'KP' || collection === 'KPR') insertOrProfile = 'BBR';
    if (collection === 'KW') insertOrProfile = 'KM02';
  }
  return {
    id: nextItemId(), kind: 'table',
    collection,
    sizeKey: matchedSize.key,
    baseCode: args.base_code || args.baseCode || 'GFM69',
    topCode,
    insertOrProfile,
    qty: args.qty || 1,
  };
}

function makeChairItem(args = {}) {
  const collection = args.collection || 'BL';
  return {
    id: nextItemId(), kind: 'chair',
    collection,
    sizeKey: args.size_key || args.sizeKey || 'reg',
    frameCode: args.frame_code || args.frameCode || (collection === 'BL' ? 'frN' : 'GFM69'),
    upholsteryCode: args.upholstery_code || args.upholsteryCode || '971',
    qty: args.qty || 6,
  };
}

/**
 * Phase 7 — manufacturing / catalogue warnings surfaced as italic rows
 * beside each item in the BOM. Pure derivations of the item config; no
 * side effects. Add new rules here as the manufacturer flags new constraints.
 */
function itemWarnings(item) {
  const out = [];
  if (item.kind === 'table') {
    const size = SIZES_BY_COLLECTION[item.collection]?.find(s => s.key === item.sizeKey);
    if (item.collection === 'KPR') {
      out.push('KPR — no EUR catalogue price; quote on request from manufacturer');
    }
    if (size?.variant === 'Y') {
      out.push('Y-variant — non-standard packaging (m³=0), ships custom');
    }
    if (item.collection === 'RND' && item.sizeKey === 'ø140' && (item.topCode === 'NC' || item.topCode === 'RB')) {
      const tier = BASE_BY_CODE[item.baseCode]?.tier;
      if (tier !== 'A') out.push('Wood ø140 — only tier-A bases (GFM69 / GFM73) are offered');
    }
    if ((size?.w || 0) >= 300 || (size?.diameter || 0) >= 220) {
      out.push('Large size — 6+ parcels, extended lead time');
    }
  }
  if (item.kind === 'chair') {
    if (item.upholsteryCode === 'COM' || item.upholsteryCode === 'COL') {
      out.push('Customer-supplied material — verify fabric quantity per BELINDA spec sheet (C.O.M. 130×140 cm / C.O.L. 1.65 sqm per chair)');
    }
    if (item.qty >= 8) out.push('Large chair order — ships in multiple boxes');
  }
  return out;
}

function itemUnitPrice(item) {
  if (item.kind === 'table') return lookupPrice({
    collection: item.collection, sizeKey: item.sizeKey,
    topCode: item.topCode, baseCode: item.baseCode,
  });
  if (item.kind === 'chair') return lookupChairPrice({
    collection: item.collection, sizeKey: item.sizeKey,
    upholsteryCode: item.upholsteryCode,
  });
  return null;
}

function itemSku(item) {
  if (item.kind === 'table') return buildSku({
    collection: item.collection, sizeKey: item.sizeKey,
    baseCode: item.baseCode, topCode: item.topCode,
    insertOrProfile: item.insertOrProfile,
  });
  if (item.kind === 'chair') return buildChairSku({
    collection: item.collection, sizeKey: item.sizeKey,
    frameCode: item.frameCode, upholsteryCode: item.upholsteryCode,
  });
  return '—';
}

function itemLabel(item) {
  if (item.kind === 'table') {
    const col = COLLECTION_BY_CODE[item.collection]?.name || item.collection;
    return `${col} · ${item.sizeKey}`;
  }
  if (item.kind === 'chair') {
    const col = CHAIR_COLLECTIONS.find(c => c.code === item.collection)?.name || item.collection;
    return `${col} · ${item.sizeKey === 'B' ? 'with arms' : 'no arms'}`;
  }
  return 'Item';
}

// ═══════════════════════════════════════════════════════════════════════════
//  EXPORT — PDF (via window.print), XLSX (SheetJS), JSON save/load
// ═══════════════════════════════════════════════════════════════════════════

function exportXLSX(project, items, total) {
  const headers = ['#', 'Kind', 'SKU', 'Description', 'Qty', 'Unit (EUR)', 'Line (EUR)'];
  const rows = items.map((it, i) => {
    const unit = itemUnitPrice(it);
    const line = unit == null ? null : unit * (it.qty || 1);
    return [
      i + 1, it.kind, itemSku(it),
      `${itemLabel(it)} · ${describeItem(it)}`,
      it.qty, unit, line,
    ];
  });
  const data = [
    ['Cattelan Italia — SKORPIO + BELINDA configuration'],
    [],
    ['Project',  project.name     || ''],
    ['Client',   project.client   || ''],
    ['Designer', project.designer || ''],
    ['Address',  project.address  || ''],
    ['Date',     project.date     || ''],
    ['Status',   project.status   || ''],
    ['Notes',    project.notes    || ''],
    [],
    headers,
    ...rows,
    [],
    ['', '', '', '', '', 'Total (EUR)', total],
  ];
  const ws = XLSX.utils.aoa_to_sheet(data);
  ws['!cols'] = [{ wch: 4 }, { wch: 8 }, { wch: 32 }, { wch: 44 }, { wch: 6 }, { wch: 12 }, { wch: 12 }];
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'BOM');
  const fname = (project.name || 'cattelan-configuration').replace(/\s+/g, '_');
  XLSX.writeFile(wb, `${fname}.xlsx`);
}

function exportJSON(project, items) {
  const data = { version: 1, exported: new Date().toISOString(), project, items };
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${(project.name || 'cattelan-configuration').replace(/\s+/g, '_')}.json`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function importJSON(file, setProject, setItems, setSelectedItemId) {
  if (!file) return;
  const reader = new FileReader();
  reader.onload = () => {
    try {
      const data = JSON.parse(reader.result);
      if (data.version !== 1) throw new Error(`Unsupported file version: ${data.version}`);
      setProject(data.project || {});
      // Re-mint IDs so they can't collide with current session counter.
      const rehydrated = (data.items || []).map(it => ({ ...it, id: nextItemId() }));
      setItems(rehydrated);
      setSelectedItemId(rehydrated[0]?.id ?? null);
    } catch (e) {
      alert('Failed to load configuration: ' + e.message);
    }
  };
  reader.readAsText(file);
}

// All upholstery codes for chairs — flat list with metadata
const ALL_UPHOLSTERY = [
  ...CHAIR_FABRICS.map(c => ({ code: c, name: '',                  group: 'fabric_micronubuck', family: 'fabric',             dir: 'chair/fabric' })),
  ...CHAIR_MICRO_NUBUCK.map(x => ({ ...x,                           group: 'fabric_micronubuck', family: 'micro-nubuck',       dir: 'chair/micro-nubuck' })),
  ...CHAIR_SYNTHETIC_LEATHER.map(x => ({ ...x,                      group: 'synthetic_leather',  family: 'synthetic-leather',  dir: 'chair/synthetic-leather' })),
  ...CHAIR_SOFT_LEATHER.map(x => ({ ...x,                           group: 'soft_leather',       family: 'soft-leather',       dir: 'chair/soft-leather' })),
  ...CHAIR_SOFT_LEATHER_GLOVE.map(x => ({ ...x,                     group: 'soft_leather_glove', family: 'soft-leather-glove', dir: 'chair/soft-leather-glove' })),
  ...CHAIR_CUSTOM.map(x => ({ code: x.code, name: x.name,           group: x.pricesAs,           family: 'custom',             dir: null })),
];

// ═══════════════════════════════════════════════════════════════════════════
//  ROOT
// ═══════════════════════════════════════════════════════════════════════════

export default function ConfiguratorApp() {
  const [project, setProject] = useState({
    name: '', client: '', designer: '', address: '',
    date: new Date().toISOString().slice(0, 10),
    status: 'draft', notes: '',
  });

  const [items, setItems] = useState(() => [makeTableItem({ collection: 'CL' }), makeChairItem({ collection: 'BL' })]);
  const [selectedItemId, setSelectedItemId] = useState(() => items[0]?.id ?? null);
  const [selectedInstanceIndex, setSelectedInstanceIndex] = useState(0);
  const [view, setView] = useState('3d');
  const [rightTab, setRightTab] = useState('spec');
  const [lang, setLang] = useState('en');

  // Unified selector: sets both id and per-instance index. AI tools and the
  // left-panel list use this with default instanceIdx=0.
  const selectItem = useCallback((id, instanceIdx = 0) => {
    setSelectedItemId(id);
    setSelectedInstanceIndex(instanceIdx);
  }, []);

  const selectedItem = useMemo(() => items.find(i => i.id === selectedItemId) || null, [items, selectedItemId]);

  const updateItem = useCallback((id, patch) => {
    setItems(prev => {
      const oldItem = prev.find(it => it.id === id);
      if (!oldItem) return prev;
      // When a table's collection or sizeKey changes the floor footprint
      // changes too — wipe any frozen chair positions/rotations so chairs
      // re-auto-layout around the new table shape.
      const isTable = oldItem.kind === 'table';
      const shapeChanged = isTable &&
        (('collection' in patch && patch.collection !== oldItem.collection) ||
         ('sizeKey'    in patch && patch.sizeKey    !== oldItem.sizeKey));
      return prev.map(it => {
        if (it.id === id) return { ...it, ...patch };
        if (shapeChanged && it.kind === 'chair' && (it.positions || it.rotations)) {
          const { positions, rotations, ...rest } = it;
          return rest;
        }
        return it;
      });
    });
  }, []);

  // Plain "add table / add chair" buttons in the left panel.
  const addItem = useCallback((kind) => {
    const newItem = kind === 'table' ? makeTableItem() : makeChairItem();
    setItems(prev => [...prev, newItem]);
    setSelectedItemId(newItem.id);
  }, []);

  // AI tool entry points — accept the full arg dict and return new id.
  const addTable = useCallback((args) => {
    const item = makeTableItem(args || {});
    setItems(prev => [...prev, item]);
    setSelectedItemId(item.id);
    return item.id;
  }, []);
  const addChair = useCallback((args) => {
    const item = makeChairItem(args || {});
    setItems(prev => [...prev, item]);
    setSelectedItemId(item.id);
    return item.id;
  }, []);

  const removeItem = useCallback((id) => {
    setItems(prev => {
      const next = prev.filter(it => it.id !== id);
      if (selectedItemId === id) setSelectedItemId(next[0]?.id ?? null);
      return next;
    });
  }, [selectedItemId]);

  const total = useMemo(() => items.reduce((sum, it) => {
    const p = itemUnitPrice(it);
    return sum + (p == null ? 0 : p * (it.qty || 1));
  }, 0), [items]);

  // AI Render: rendererRef is populated by View3D's effect; capture() grabs a
  // fresh frame and returns base64 JPEG (raw, no "data:" prefix — server requires).
  const rendererRef = useRef(null);
  const sceneRef = useRef(null);
  const cameraRef = useRef(null);
  const [renderJob, setRenderJob] = useState(null);   // { snapshot, prompt } | null

  const captureSnapshot = useCallback(() => {
    const renderer = rendererRef.current;
    const scene = sceneRef.current;
    const camera = cameraRef.current;
    if (!renderer || !scene || !camera) return null;
    // Force a fresh render so the framebuffer reflects current camera state,
    // then read it before the browser composites.
    renderer.render(scene, camera);
    const dataUrl = renderer.domElement.toDataURL('image/jpeg', 0.85);
    const comma = dataUrl.indexOf(',');
    return comma > 0 ? dataUrl.slice(comma + 1) : null;
  }, []);

  const startRender = useCallback(() => {
    const snapshot = captureSnapshot();
    if (!snapshot) return;
    const prompt = buildRenderPrompt(items);
    setRenderJob({ snapshot, prompt, key: Date.now() });
  }, [captureSnapshot, items]);

  return (
    <div className="cfg-app">
      <Header
        project={project} setProject={setProject}
        items={items} setItems={setItems}
        setSelectedItemId={setSelectedItemId}
        total={total}
        lang={lang} setLang={setLang}
        onAIRender={startRender}
        canAIRender={items.length > 0 && view === '3d'}
      />
      <div className="cfg-main">
        <LeftPanel
          items={items} selectedItemId={selectedItemId} setSelectedItemId={selectItem}
          updateItem={updateItem} addItem={addItem} removeItem={removeItem}
          lang={lang}
        />
        <CenterPanel
          items={items} selectedItem={selectedItem}
          selectedItemId={selectedItemId}
          selectedInstanceIndex={selectedInstanceIndex}
          selectItem={selectItem}
          updateItem={updateItem}
          view={view} setView={setView} lang={lang}
          rendererRef={rendererRef}
          sceneRef={sceneRef}
          cameraRef={cameraRef}
        />
        <RightPanel
          tab={rightTab} setTab={setRightTab}
          items={items} selectedItem={selectedItem} total={total}
          selectedItemId={selectedItemId}
          project={project} setProject={setProject}
          lang={lang}
          ai={{ addTable, addChair, updateItem, removeItem, selectItem: (id) => selectItem(id, 0) }}
        />
      </div>
      <PrintLayout project={project} items={items} total={total} />
      {renderJob && (
        <RenderModal
          key={renderJob.key}
          snapshotBase64={renderJob.snapshot}
          prompt={renderJob.prompt}
          onClose={() => setRenderJob(null)}
          onRegenerate={() => startRender()}
          lang={lang}
        />
      )}
      <ConfiguratorStyles />
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
//  HEADER
// ═══════════════════════════════════════════════════════════════════════════

function Header({ project, setProject, items, setItems, setSelectedItemId, total, lang, setLang, onAIRender, canAIRender }) {
  const fileInputRef = useRef(null);
  return (
    <header className="cfg-header">
      <div className="cfg-brand">
        <span className="cfg-brand-mark">{t('brand.title', lang)}</span>
        <span className="cfg-brand-sub">{t('brand.subtitle', lang)}</span>
      </div>
      <div className="cfg-project-strip">
        <input
          type="text" placeholder={t('project.name', lang)}
          value={project.name}
          onChange={e => setProject(p => ({ ...p, name: e.target.value }))}
          className="cfg-input cfg-input-name"
        />
        <input
          type="text" placeholder={t('project.client', lang)}
          value={project.client}
          onChange={e => setProject(p => ({ ...p, client: e.target.value }))}
          className="cfg-input"
        />
        <span className={`cfg-status cfg-status-${project.status}`}>{project.status}</span>
      </div>
      <div className="cfg-header-actions">
        <span className="cfg-total"><strong>{fmtEUR(total)}</strong> {t('project.total', lang).toLowerCase()}</span>
        <select className="cfg-lang-picker" value={lang} onChange={e => setLang(e.target.value)}>
          {LANGS.map(l => <option key={l.code} value={l.code}>{l.label}</option>)}
        </select>
        <button
          className="cfg-btn cfg-btn-ai"
          onClick={onAIRender}
          disabled={!canAIRender}
          title={t('action.ai_render', lang)}
        >
          <Sparkles size={14} /> {t('action.ai_render', lang)}
        </button>
        <button className="cfg-btn" onClick={() => window.print()} title="Print or save as PDF">
          <Printer size={14} /> {t('action.pdf', lang)}
        </button>
        <button className="cfg-btn" onClick={() => exportXLSX(project, items, total)} title="Export BOM as XLSX">
          <FileDown size={14} /> {t('action.xlsx', lang)}
        </button>
        <button className="cfg-btn" onClick={() => exportJSON(project, items)} title="Save configuration as JSON">
          <Save size={14} /> {t('action.save', lang)}
        </button>
        <button className="cfg-btn" onClick={() => fileInputRef.current?.click()} title="Load configuration from JSON">
          <FolderOpen size={14} /> {t('action.load', lang)}
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept=".json,application/json"
          style={{ display: 'none' }}
          onChange={e => {
            importJSON(e.target.files?.[0], setProject, setItems, setSelectedItemId);
            e.target.value = '';
          }}
        />
      </div>
    </header>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
//  LEFT PANEL
// ═══════════════════════════════════════════════════════════════════════════

function LeftPanel({ items, selectedItemId, setSelectedItemId, updateItem, addItem, removeItem }) {
  const selectedItem = items.find(i => i.id === selectedItemId);
  return (
    <aside className="cfg-left">
      <div className="cfg-section">
        <div className="cfg-section-header">Items</div>
        <div className="cfg-items-list">
          {items.map(item => (
            <ItemRow
              key={item.id} item={item}
              active={item.id === selectedItemId}
              onSelect={() => setSelectedItemId(item.id)}
              onRemove={() => removeItem(item.id)}
              onQtyChange={(q) => updateItem(item.id, { qty: q })}
            />
          ))}
          {!items.length && <div className="cfg-empty">No items yet — add one below.</div>}
        </div>
        <div className="cfg-add-buttons">
          <button className="cfg-btn cfg-btn-add" onClick={() => addItem('table')}>
            <TableIcon size={14} /> Add table
          </button>
          <button className="cfg-btn cfg-btn-add" onClick={() => addItem('chair')}>
            <Armchair size={14} /> Add chair
          </button>
        </div>
      </div>

      {selectedItem && (
        <div className="cfg-section">
          <div className="cfg-section-header">Configure</div>
          {selectedItem.kind === 'table'
            ? <TableEditor item={selectedItem} updateItem={(p) => updateItem(selectedItem.id, p)} />
            : <ChairEditor item={selectedItem} updateItem={(p) => updateItem(selectedItem.id, p)} />}
        </div>
      )}
    </aside>
  );
}

function ItemRow({ item, active, onSelect, onRemove, onQtyChange }) {
  const Icon = item.kind === 'table' ? TableIcon : Armchair;
  const price = itemUnitPrice(item);
  const lineTotal = price == null ? null : price * (item.qty || 1);
  return (
    <div className={`cfg-item-row ${active ? 'active' : ''}`} onClick={onSelect}>
      <Icon size={14} className="cfg-item-icon" />
      <div className="cfg-item-meta">
        <div className="cfg-item-label">{itemLabel(item)}</div>
        <div className="cfg-item-sku">{itemSku(item)}</div>
      </div>
      <input
        type="number" min="1" max="999"
        value={item.qty}
        onChange={e => onQtyChange(Math.max(1, +e.target.value || 1))}
        onClick={e => e.stopPropagation()}
        className="cfg-item-qty"
        title="Quantity"
      />
      <div className="cfg-item-price">{fmtEUR(lineTotal)}</div>
      <button
        className="cfg-icon-btn"
        onClick={e => { e.stopPropagation(); onRemove(); }}
        title="Remove"
      ><Trash2 size={12} /></button>
    </div>
  );
}

// ─── Table editor ──────────────────────────────────────────────────────────

function TableEditor({ item, updateItem }) {
  const collection = item.collection;
  const sizes = SIZES_BY_COLLECTION[collection] || [];
  const currentSize = sizes.find(s => s.key === item.sizeKey) || sizes[0];
  const tops = currentSize?.tops || [];
  const bases = availableBases(collection, item.sizeKey, item.topCode);
  const insertsOrProfiles = availableInsertsOrProfiles(collection, item.sizeKey);

  const onCollectionChange = (newColl) => {
    const sizes2 = SIZES_BY_COLLECTION[newColl] || [];
    const size2 = sizes2[0];
    const top2 = size2?.tops?.[0];
    const patch = { collection: newColl, sizeKey: size2?.key, topCode: top2 };
    if (newColl === 'KP' || newColl === 'KPR') patch.insertOrProfile = 'BBR';
    else if (newColl === 'KW') patch.insertOrProfile = 'KM02';
    else patch.insertOrProfile = undefined;
    updateItem(patch);
  };

  const onSizeChange = (newSize) => {
    const sz = sizes.find(s => s.key === newSize);
    const top2 = sz?.tops?.includes(item.topCode) ? item.topCode : sz?.tops?.[0];
    updateItem({ sizeKey: newSize, topCode: top2 });
  };

  const onTopChange = (newTop) => {
    const okBases = availableBases(collection, item.sizeKey, newTop);
    const base2 = okBases.includes(item.baseCode) ? item.baseCode : okBases[0];
    updateItem({ topCode: newTop, baseCode: base2 });
  };

  return (
    <div className="cfg-editor">
      <Field label="Collection">
        <select value={collection} onChange={e => onCollectionChange(e.target.value)}>
          {COLLECTIONS.map(c => (
            <option key={c.code} value={c.code}>
              {c.name} ({c.year}){c.legacy ? ' · legacy' : ''}
            </option>
          ))}
        </select>
      </Field>

      <Field label="Size">
        <select value={item.sizeKey} onChange={e => onSizeChange(e.target.value)}>
          {sizes.map(s => {
            const dim = s.diameter ? `ø${s.diameter}` : `${s.w}×${s.d}`;
            const var_ = s.variant ? ` ${s.variant}` : '';
            return <option key={s.key} value={s.key}>{dim}{var_} · h{s.h}</option>;
          })}
        </select>
      </Field>

      <Field label="Top">
        <select value={item.topCode} onChange={e => onTopChange(e.target.value)}>
          {tops.map(t => <option key={t} value={t}>{t} — {topDisplay(t)}</option>)}
        </select>
      </Field>

      {(collection === 'KW' || collection === 'KP' || collection === 'KPR') && (
        <Field label={collection === 'KW' ? 'Ceramic insert' : 'Brushed profile'}>
          <select
            value={item.insertOrProfile || ''}
            onChange={e => updateItem({ insertOrProfile: e.target.value })}
          >
            {insertsOrProfiles.map(c => (
              <option key={c} value={c}>
                {c}{collection === 'KW' ? ` — ${ceramicName(c)}` : c === 'BBR' ? ' — Brushed Bronze' : ' — Brushed Grey'}
              </option>
            ))}
          </select>
        </Field>
      )}

      <Field label="Base">
        <SwatchGrid
          options={bases.map(b => {
            const meta = BASE_BY_CODE[b];
            return { code: b, label: `${b} — ${meta.name} (tier ${meta.tier})`, hex: meta.hex, url: photoTextureUrl('metal', b) };
          })}
          value={item.baseCode}
          onChange={(v) => updateItem({ baseCode: v })}
        />
      </Field>
    </div>
  );
}

function topDisplay(code) {
  const lookup = [...GLASS_TOPS, ...CRYSTALART_TOPS, ...CERAMIC_TOPS, ...WOOD_TOPS];
  const m = lookup.find(t => t.code === code);
  return m?.name || code;
}

function ceramicName(code) {
  return CERAMIC_TOPS.find(c => c.code === code)?.name || code;
}

// ─── Chair editor ──────────────────────────────────────────────────────────

function ChairEditor({ item, updateItem }) {
  const frames = CHAIR_FRAMES[item.collection] || [];
  const [groupFilter, setGroupFilter] = useState('all');
  const [search, setSearch] = useState('');

  const onCollectionChange = (newColl) => {
    const frames2 = CHAIR_FRAMES[newColl] || [];
    updateItem({ collection: newColl, frameCode: frames2[0]?.code });
  };

  const filteredUphol = useMemo(() => {
    const q = search.trim().toLowerCase();
    return ALL_UPHOLSTERY.filter(u => {
      if (groupFilter !== 'all' && u.group !== groupFilter) return false;
      if (!q) return true;
      return u.code.toLowerCase().includes(q) || (u.name || '').toLowerCase().includes(q);
    });
  }, [groupFilter, search]);

  return (
    <div className="cfg-editor">
      <Field label="Collection">
        <select value={item.collection} onChange={e => onCollectionChange(e.target.value)}>
          {CHAIR_COLLECTIONS.map(c => (
            <option key={c.code} value={c.code}>{c.name} ({c.year}) · {c.frame}</option>
          ))}
        </select>
      </Field>

      <Field label="Armrests">
        <div className="cfg-toggle-group">
          {CHAIR_SIZES.map(s => (
            <button
              key={s.key}
              className={`cfg-toggle ${item.sizeKey === s.key ? 'active' : ''}`}
              onClick={() => updateItem({ sizeKey: s.key })}
            >{s.withArms ? 'With arms (B)' : 'No arms'}</button>
          ))}
        </div>
      </Field>

      <Field label="Frame finish">
        <SwatchGrid
          options={frames.map(f => ({
            code: f.code, label: f.name, hex: f.hex,
            url: photoTextureUrl(item.collection === 'BL' ? 'wood' : 'metal', f.code),
          }))}
          value={item.frameCode}
          onChange={(v) => updateItem({ frameCode: v })}
        />
      </Field>

      <Field label="Upholstery">
        <div className="cfg-uphol-filter">
          <select value={groupFilter} onChange={e => setGroupFilter(e.target.value)}>
            <option value="all">All groups</option>
            {CHAIR_UPHOLSTERY_GROUPS.map(g => (
              <option key={g} value={g}>{CHAIR_UPHOLSTERY_GROUP_LABEL[g]}</option>
            ))}
          </select>
          <div className="cfg-search">
            <Search size={12} />
            <input
              type="text"
              placeholder="Filter by code or name..."
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>
        </div>
        <div className="cfg-uphol-grid">
          {filteredUphol.slice(0, 60).map(u => (
            <div
              key={u.code + ':' + u.group}
              className={`cfg-uphol-cell ${item.upholsteryCode === u.code ? 'active' : ''}`}
              onClick={() => updateItem({ upholsteryCode: u.code })}
              title={`${u.code} · ${u.name || ''} (${CHAIR_UPHOLSTERY_GROUP_LABEL[u.group]})`}
            >
              {u.dir
                ? <img src={`/textures/materials/${u.dir}/${u.code}.jpg`} alt={u.code} loading="lazy" />
                : <div className="cfg-uphol-placeholder">{u.code}</div>}
              <div className="cfg-uphol-caption">{u.code}</div>
            </div>
          ))}
          {filteredUphol.length > 60 && (
            <div className="cfg-uphol-more">+{filteredUphol.length - 60} more — refine the filter</div>
          )}
        </div>
      </Field>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div className="cfg-field">
      <label>{label}</label>
      {children}
    </div>
  );
}

function SwatchGrid({ options, value, onChange }) {
  return (
    <div className="cfg-swatch-grid">
      {options.map(o => (
        <div
          key={o.code}
          className={`cfg-swatch ${value === o.code ? 'active' : ''}`}
          onClick={() => onChange(o.code)}
          title={o.label}
        >
          {o.url
            ? <img src={o.url} alt={o.code} loading="lazy" />
            : <div className="cfg-swatch-color" style={{ background: o.hex }} />}
          <span className="cfg-swatch-caption">{o.code}</span>
        </div>
      ))}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
//  CENTER PANEL
// ═══════════════════════════════════════════════════════════════════════════

function CenterPanel({ items, selectedItem, selectedItemId, selectedInstanceIndex, selectItem, updateItem, view, setView, lang, rendererRef, sceneRef, cameraRef }) {
  return (
    <main className="cfg-center">
      <div className="cfg-view-tabs">
        {['3d', 'spec', 'plan'].map(v => (
          <button
            key={v}
            className={`cfg-tab ${view === v ? 'active' : ''}`}
            onClick={() => setView(v)}
            disabled={v === 'plan'}
          >{t('view.' + v, lang)}</button>
        ))}
      </div>
      <div className="cfg-view-body">
        {view === '3d'   && <View3D
          items={items}
          selectedItemId={selectedItemId}
          selectedInstanceIndex={selectedInstanceIndex}
          selectItem={selectItem}
          updateItem={updateItem}
          rendererRef={rendererRef}
          sceneRef={sceneRef}
          cameraRef={cameraRef}
        />}
        {view === 'spec' && <ViewSpec items={items} lang={lang} />}
        {view === 'plan' && <div className="cfg-placeholder">Plan view — coming later</div>}
      </div>
    </main>
  );
}

// ─── Scene layout — table(s) + chairs around the first table ──────────────
//
// Each table is a "station"; chairs are distributed evenly along its long
// sides (positive-Z far, negative-Z near). Multiple tables sit side-by-side
// along X with a 400 mm gap. If there are chair items without a table, they
// stand in a single row.

function tableDimsMm(item) {
  const sizes = SIZES_BY_COLLECTION[item.collection] || [];
  const size = sizes.find(s => s.key === item.sizeKey) || sizes[0];
  if (!size) return { w: 2000, d: 1000, shape: 'rect' };
  return {
    w: (size.diameter || size.w) * 10,
    d: (size.diameter || size.d) * 10,
    shape: size.shape,
  };
}

/**
 * Distribute N chairs around a rectangular table:
 *  1. Long sides first (alternating far/near for symmetry)
 *  2. Then short ends (alternating right/left)
 *  3. Overflow squeezed onto long sides
 * Returns array of { side, indexOnSide, sideCount } per chair (length N).
 */
function distributeRectangular(N, tableW, tableD, chairW, gap) {
  const fitsLong  = Math.max(1, Math.floor(tableW / (chairW + gap)) - 1);
  const fitsShort = Math.max(0, Math.floor(tableD / (chairW + gap)) - 1);
  const counts = { far: 0, near: 0, right: 0, left: 0 };
  let rem = N;
  for (let i = 0; i < 2 * fitsLong && rem > 0; i++) {
    const side = i % 2 === 0 ? 'far' : 'near';
    if (counts[side] < fitsLong) { counts[side]++; rem--; }
  }
  for (let i = 0; i < 2 * fitsShort && rem > 0; i++) {
    const side = i % 2 === 0 ? 'right' : 'left';
    if (counts[side] < fitsShort) { counts[side]++; rem--; }
  }
  while (rem > 0) {
    counts.far++; rem--;
    if (rem > 0) { counts.near++; rem--; }
  }
  const out = [];
  for (const side of ['far', 'near', 'right', 'left']) {
    const n = counts[side];
    for (let i = 0; i < n; i++) {
      out.push({ side, indexOnSide: i, sideCount: n });
    }
  }
  return out;
}

function layoutItems(items) {
  const tables = items.filter(i => i.kind === 'table');
  const chairs = items.filter(i => i.kind === 'chair');
  const out = [];

  // ── Tables — `item.position` if dragged, otherwise auto side-by-side ──
  const tDims = tables.map(tableDimsMm);
  const totalTableW = tDims.reduce((s, d) => s + d.w, 0) + Math.max(0, tables.length - 1) * 400;
  let xCursor = -totalTableW / 2;
  tables.forEach((tbl, i) => {
    const d = tDims[i];
    const autoX = xCursor + d.w / 2;
    out.push({
      item: tbl,
      instanceIndex: 0,
      position: [tbl.position?.x ?? autoX, 0, tbl.position?.z ?? 0],
      rotY: tbl.rotation ?? 0,
    });
    xCursor += d.w + 400;
  });

  // ── Chairs ───────────────────────────────────────────────────────────
  //
  // For a chair item with `qty=N`:
  //   - If positions[]/rotations[] are populated → MANUAL cluster, use stored values.
  //   - Else auto-layout around the first table:
  //       * round table  → N chairs equally spaced around the perimeter,
  //                        each facing the table centre
  //       * rect/shaped  → fill long sides first, then short ends, then
  //                        overflow back onto long sides
  //   - No table       → row layout
  const CHAIR_DEPTH = 590;
  const CHAIR_GAP   = 50;    // mm between chair back and table edge (tight)
  const MIN_CHAIR_GAP = 50;  // min mm between adjacent chair edges (for fit calc)
  const CHAIR_WIDTH = 535;   // BELINDA reference
  const firstTableLayout = out[0];
  const firstTableDims   = tDims[0];

  chairs.forEach(chairItem => {
    const total = Math.max(1, chairItem.qty || 1);
    const positions = chairItem.positions || [];
    const rotations = chairItem.rotations || [];
    const isManual = positions.some(p => p) || rotations.length > 0;

    if (isManual) {
      for (let i = 0; i < total; i++) {
        const p = positions[i] || { x: 0, z: 0 };
        const r = rotations[i] ?? 0;
        out.push({ item: chairItem, instanceIndex: i, position: [p.x, 0, p.z], rotY: r });
      }
      return;
    }

    if (!firstTableLayout) {
      const SP = 700;
      for (let i = 0; i < total; i++) {
        out.push({ item: chairItem, instanceIndex: i, position: [(i - (total - 1) / 2) * SP, 0, 0], rotY: 0 });
      }
      return;
    }

    const fx = firstTableLayout.position[0];
    const fz = firstTableLayout.position[2];
    const td = firstTableDims;

    if (td.shape === 'round') {
      // Radial layout — equally spaced around the table circumference.
      const r = td.w / 2 + CHAIR_DEPTH / 2 + CHAIR_GAP;
      for (let i = 0; i < total; i++) {
        const angle = (i / total) * 2 * Math.PI - Math.PI / 2;   // start at "north"
        out.push({
          item: chairItem,
          instanceIndex: i,
          position: [fx + Math.cos(angle) * r, 0, fz + Math.sin(angle) * r],
          rotY: -angle - Math.PI / 2,   // face the centre
        });
      }
    } else {
      // Rect/shaped — distribute across all 4 sides.
      const placements = distributeRectangular(total, td.w, td.d, CHAIR_WIDTH, MIN_CHAIR_GAP);
      placements.forEach((pl, i) => {
        let x, z, rotY;
        const offset = (pl.indexOnSide + 1) / (pl.sideCount + 1);
        if (pl.side === 'far') {
          x = fx - td.w / 2 + offset * td.w;
          z = fz + td.d / 2 + CHAIR_DEPTH / 2 + CHAIR_GAP;
          rotY = Math.PI;
        } else if (pl.side === 'near') {
          x = fx - td.w / 2 + offset * td.w;
          z = fz - (td.d / 2 + CHAIR_DEPTH / 2 + CHAIR_GAP);
          rotY = 0;
        } else if (pl.side === 'right') {
          x = fx + td.w / 2 + CHAIR_DEPTH / 2 + CHAIR_GAP;
          z = fz - td.d / 2 + offset * td.d;
          rotY = -Math.PI / 2;
        } else { // 'left'
          x = fx - (td.w / 2 + CHAIR_DEPTH / 2 + CHAIR_GAP);
          z = fz - td.d / 2 + offset * td.d;
          rotY = Math.PI / 2;
        }
        out.push({ item: chairItem, instanceIndex: i, position: [x, 0, z], rotY });
      });
    }
  });

  return out;
}

// ─── View3D — multi-item scene + click-to-select + selection highlight ────

function View3D({ items, selectedItemId, selectedInstanceIndex, selectItem, updateItem, rendererRef, sceneRef, cameraRef }) {
  const mountRef = useRef(null);
  const stateRef = useRef(null);

  useEffect(() => {
    const mount = mountRef.current;
    if (!mount) return;
    const w = mount.clientWidth || 600;
    const h = mount.clientHeight || 500;

    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0xf3eee4);
    const camera = new THREE.PerspectiveCamera(35, w / h, 10, 50000);

    // preserveDrawingBuffer keeps the framebuffer readable after render so
    // canvas.toDataURL works for the AI Render snapshot. Without it, toDataURL
    // returns a blank image on most browsers (chromium clears after composite).
    const renderer = new THREE.WebGLRenderer({ antialias: true, preserveDrawingBuffer: true });
    renderer.setSize(w, h);
    renderer.setPixelRatio(window.devicePixelRatio || 1);
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    renderer.outputEncoding = THREE.sRGBEncoding;
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.05;
    mount.appendChild(renderer.domElement);

    // Expose to the parent for the AI Render snapshot. Cleared on unmount below.
    if (rendererRef) rendererRef.current = renderer;
    if (sceneRef)    sceneRef.current = scene;
    if (cameraRef)   cameraRef.current = camera;

    scene.add(new THREE.HemisphereLight(0xfff5e0, 0xb8a98a, 0.6));
    const key = new THREE.DirectionalLight(0xffffff, 1.0);
    key.position.set(2000, 3000, 1500); key.castShadow = true;
    key.shadow.mapSize.width = 2048; key.shadow.mapSize.height = 2048;
    key.shadow.camera.left = -5000; key.shadow.camera.right = 5000;
    key.shadow.camera.top = 5000;   key.shadow.camera.bottom = -5000;
    scene.add(key);
    const fill = new THREE.DirectionalLight(0xd6e0ff, 0.3);
    fill.position.set(-2000, 1500, -1000);
    scene.add(fill);

    const ground = new THREE.Mesh(
      new THREE.PlaneGeometry(20000, 20000),
      new THREE.MeshStandardMaterial({ color: 0xe6dfd1, roughness: 0.95 }),
    );
    ground.rotation.x = -Math.PI / 2;
    ground.receiveShadow = true;
    scene.add(ground);

    // Mutable scene state — refs are bumped from prop-watcher effects below.
    stateRef.current = {
      scene, camera, renderer, mount,
      itemGroups: [],
      selectionHelper: null,
      selectRef: selectItem,
      updateItemRef: updateItem,
      itemsRef: items,
      selectedItemIdRef: selectedItemId,
      selectedInstanceIndexRef: selectedInstanceIndex,
      cameraFitted: false,   // first items-effect run sets this true
    };

    // ── Camera (orbit) ──────────────────────────────────────────────────
    let azimuth = Math.PI * 0.25, elevation = 0.55, radius = 4500;
    const lookY = 500;
    const updateCam = () => {
      camera.position.x = Math.cos(azimuth) * Math.cos(elevation) * radius;
      camera.position.y = Math.sin(elevation) * radius + lookY;
      camera.position.z = Math.sin(azimuth) * Math.cos(elevation) * radius;
      camera.lookAt(0, lookY, 0);
    };
    stateRef.current.setRadius = (r) => { radius = r; updateCam(); };
    updateCam();

    // ── Mouse interaction state machine ────────────────────────────────
    //   idle  →  orbit          (mousedown on empty space)
    //         →  press_item     (mousedown on an item)
    //   press_item →  drag      (mousemove > 5 px)
    //              →  click     (mouseup with no real movement → select)
    //   any   →  idle           (mouseup)
    //
    // BOTH tables and chairs are draggable. A dragged TABLE pulls its
    // auto-following chairs with it (chairs whose item has no own .position).
    // Once a chair is dragged it gains its own .position and becomes
    // independent of the table.
    const groundPlane = new THREE.Plane(new THREE.Vector3(0, 1, 0), 0);
    let mode = 'idle';                   // 'idle' | 'orbit' | 'press_item' | 'drag'
    let orbitLastX = 0, orbitLastY = 0;
    let press = null;                    // null or { itemId, kind, mouseX, mouseY, ground, groupStarts: Map }

    const mouseToGround = (clientX, clientY) => {
      const rect = renderer.domElement.getBoundingClientRect();
      const ndc = new THREE.Vector2(
        ((clientX - rect.left) / rect.width) * 2 - 1,
        -((clientY - rect.top) / rect.height) * 2 + 1,
      );
      const rc = new THREE.Raycaster();
      rc.setFromCamera(ndc, camera);
      const out = new THREE.Vector3();
      const hit = rc.ray.intersectPlane(groundPlane, out);
      return hit ? out : null;
    };
    const hitItemGroup = (clientX, clientY) => {
      const rect = renderer.domElement.getBoundingClientRect();
      const ndc = new THREE.Vector2(
        ((clientX - rect.left) / rect.width) * 2 - 1,
        -((clientY - rect.top) / rect.height) * 2 + 1,
      );
      const rc = new THREE.Raycaster();
      rc.setFromCamera(ndc, camera);
      const hits = rc.intersectObjects(stateRef.current.itemGroups, true);
      for (const hit of hits) {
        let o = hit.object;
        while (o) {
          if (o.userData?.itemId != null) return o;
          o = o.parent;
        }
      }
      return null;
    };

    // Axis-aligned XZ overlap helper for collision detection.
    const aabbXZ = (group) => {
      const b = new THREE.Box3().setFromObject(group);
      return { minX: b.min.x, maxX: b.max.x, minZ: b.min.z, maxZ: b.max.z };
    };
    // Allowed overlap (mm) between AABBs of two kinds before they "collide".
    // Lets chairs tuck under the table top while still preventing chair-chair
    // intersection and table-table intersection.
    const overlapAllowance = (kindA, kindB) => {
      if ((kindA === 'table' && kindB === 'chair') ||
          (kindA === 'chair' && kindB === 'table')) return 300;
      return 0;
    };
    const collidesWithAllowance = (a, b, allow) => (
      a.minX < b.maxX - allow && a.maxX > b.minX + allow &&
      a.minZ < b.maxZ - allow && a.maxZ > b.minZ + allow
    );

    // Find the single dragged group: identified by (itemId, instanceIndex)
    // — chair items have many groups sharing the same itemId.
    const findDraggedGroup = () => stateRef.current.itemGroups.find(g =>
      g.userData.itemId === press.itemId &&
      g.userData.instanceIndex === press.instanceIndex
    );

    const onDown = e => {
      e.preventDefault();
      const hit = hitItemGroup(e.clientX, e.clientY);
      if (hit) {
        const ground = mouseToGround(e.clientX, e.clientY);
        if (!ground) { mode = 'orbit'; orbitLastX = e.clientX; orbitLastY = e.clientY; return; }
        press = {
          itemId:        hit.userData.itemId,
          instanceIndex: hit.userData.instanceIndex ?? 0,
          kind:          hit.userData.kind,
          mouseX:        e.clientX, mouseY: e.clientY,
          ground,
          startPos:      { x: hit.position.x, z: hit.position.z },
          lastValidPos:  { x: hit.position.x, z: hit.position.z },
        };
        mode = 'press_item';
      } else {
        mode = 'orbit';
        orbitLastX = e.clientX; orbitLastY = e.clientY;
      }
    };

    const onMove = e => {
      if (mode === 'orbit') {
        azimuth -= (e.clientX - orbitLastX) * 0.01;
        elevation = Math.max(0.05, Math.min(1.45, elevation - (e.clientY - orbitLastY) * 0.005));
        orbitLastX = e.clientX; orbitLastY = e.clientY;
        updateCam();
        return;
      }
      if (mode === 'press_item') {
        const moved = Math.abs(e.clientX - press.mouseX) + Math.abs(e.clientY - press.mouseY);
        if (moved > 5) {
          mode = 'drag';
          document.body.style.cursor = 'grabbing';
        } else return;
      }
      if (mode === 'drag') {
        const now = mouseToGround(e.clientX, e.clientY);
        if (!now) return;
        const dx = now.x - press.ground.x;
        const dz = now.z - press.ground.z;
        const dragged = findDraggedGroup();
        if (!dragged) return;
        const candidateX = press.startPos.x + dx;
        const candidateZ = press.startPos.z + dz;

        // Pre-compute static AABBs (+ kind) of every NON-dragged group.
        const staticAABBs = stateRef.current.itemGroups
          .filter(g => g !== dragged)
          .map(g => ({ g, aabb: aabbXZ(g), kind: g.userData.kind }));
        const draggedKind = dragged.userData.kind;

        // Probe candidate axis-by-axis: full move → X-only → Z-only → none.
        const probe = (px, pz) => {
          dragged.position.x = px;
          dragged.position.z = pz;
          dragged.updateMatrixWorld(true);
          const my = aabbXZ(dragged);
          return staticAABBs.every(({ aabb, kind }) =>
            !collidesWithAllowance(my, aabb, overlapAllowance(draggedKind, kind))
          );
        };
        let ok = probe(candidateX, candidateZ);
        if (!ok) ok = probe(candidateX, press.lastValidPos.z);
        if (!ok) ok = probe(press.lastValidPos.x, candidateZ);
        if (!ok) {
          dragged.position.x = press.lastValidPos.x;
          dragged.position.z = press.lastValidPos.z;
        } else {
          press.lastValidPos = { x: dragged.position.x, z: dragged.position.z };
        }
      }
    };

    const onUp = e => {
      if (mode === 'drag') {
        const SNAP = 50;
        const dragged = findDraggedGroup();
        if (dragged) {
          const finalX = Math.round(dragged.position.x / SNAP) * SNAP;
          const finalZ = Math.round(dragged.position.z / SNAP) * SNAP;
          const items_ = stateRef.current.itemsRef || [];
          const item = items_.find(it => it.id === press.itemId);
          if (item) {
            if (item.kind === 'table') {
              stateRef.current.updateItemRef?.(press.itemId, { position: { x: finalX, z: finalZ } });
            } else {
              // Chair: lock the WHOLE cluster at current positions, then
              // override the dragged instance with the snapped target.
              const allPositions = stateRef.current.itemGroups
                .filter(g => g.userData.itemId === press.itemId)
                .sort((a, b) => (a.userData.instanceIndex ?? 0) - (b.userData.instanceIndex ?? 0))
                .map(g => ({ x: g.position.x, z: g.position.z }));
              allPositions[press.instanceIndex] = { x: finalX, z: finalZ };
              stateRef.current.updateItemRef?.(press.itemId, { positions: allPositions });
            }
          }
        }
        document.body.style.cursor = '';
      } else if (mode === 'press_item') {
        stateRef.current.selectRef?.(press.itemId, press.instanceIndex);
      }
      mode = 'idle';
      press = null;
    };

    // ── Keyboard: R rotates the selected item ±45°. Skips when an input
    //    has focus so the editors aren't disturbed.
    const onKey = (ev) => {
      if (ev.key !== 'r' && ev.key !== 'R') return;
      const a = document.activeElement;
      if (a && (a.tagName === 'INPUT' || a.tagName === 'TEXTAREA' || a.tagName === 'SELECT' || a.isContentEditable)) return;
      ev.preventDefault();
      const delta = ev.shiftKey ? -Math.PI / 4 : Math.PI / 4;
      const items_  = stateRef.current.itemsRef || [];
      const selId   = stateRef.current.selectedItemIdRef;
      const selIdx  = stateRef.current.selectedInstanceIndexRef ?? 0;
      const item    = items_.find(it => it.id === selId);
      if (!item) return;
      if (item.kind === 'table') {
        stateRef.current.updateItemRef?.(selId, { rotation: (item.rotation || 0) + delta });
        return;
      }
      // Chair: freeze the whole cluster's current positions + rotations on
      // the first manual rotation, then bump only the selected instance.
      const groups = stateRef.current.itemGroups
        .filter(g => g.userData.itemId === selId)
        .sort((a, b) => (a.userData.instanceIndex ?? 0) - (b.userData.instanceIndex ?? 0));
      const positions = item.positions
        ? [...item.positions]
        : groups.map(g => ({ x: g.position.x, z: g.position.z }));
      const rotations = item.rotations
        ? [...item.rotations]
        : groups.map(g => g.rotation.y);
      rotations[selIdx] = (rotations[selIdx] ?? 0) + delta;
      stateRef.current.updateItemRef?.(selId, { positions, rotations });
    };
    window.addEventListener('keydown', onKey);

    const onWheel = e => {
      radius = Math.max(800, Math.min(15000, radius + e.deltaY * 4));
      updateCam();
      e.preventDefault();
    };

    const dom = renderer.domElement;
    dom.addEventListener('mousedown', onDown);
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    dom.addEventListener('wheel', onWheel, { passive: false });

    const onResize = () => {
      const w2 = mount.clientWidth, h2 = mount.clientHeight;
      camera.aspect = w2 / h2;
      camera.updateProjectionMatrix();
      renderer.setSize(w2, h2);
    };
    window.addEventListener('resize', onResize);

    let raf;
    const loop = () => { renderer.render(scene, camera); raf = requestAnimationFrame(loop); };
    loop();

    return () => {
      cancelAnimationFrame(raf);
      dom.removeEventListener('mousedown', onDown);
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      window.removeEventListener('keydown', onKey);
      dom.removeEventListener('wheel', onWheel);
      window.removeEventListener('resize', onResize);
      mount.removeChild(dom);
      renderer.dispose();
      if (rendererRef) rendererRef.current = null;
      if (sceneRef)    sceneRef.current = null;
      if (cameraRef)   cameraRef.current = null;
    };
  }, []);

  // Keep refs aligned with the latest props (handlers close over stateRef).
  useEffect(() => {
    if (stateRef.current) {
      stateRef.current.selectRef                = selectItem;
      stateRef.current.updateItemRef            = updateItem;
      stateRef.current.itemsRef                 = items;
      stateRef.current.selectedItemIdRef        = selectedItemId;
      stateRef.current.selectedInstanceIndexRef = selectedInstanceIndex;
    }
  }, [selectItem, updateItem, items, selectedItemId, selectedInstanceIndex]);

  // (Re)build all item groups whenever `items` changes.
  useEffect(() => {
    const s = stateRef.current;
    if (!s) return;
    // Tear down previous
    s.itemGroups.forEach(g => {
      s.scene.remove(g);
      g.traverse(o => {
        if (o.geometry) o.geometry.dispose();
        if (o.material && o.material.dispose) o.material.dispose();
      });
    });
    if (s.selectionHelper) {
      s.scene.remove(s.selectionHelper);
      s.selectionHelper.geometry?.dispose?.();
      s.selectionHelper.material?.dispose?.();
      s.selectionHelper = null;
    }

    const layouts = layoutItems(items);
    const placedGroups = [];
    layouts.forEach(({ item, position, rotY, instanceIndex }) => {
      const g = item.kind === 'table' ? buildTableMesh(item) : buildChairMesh(item);
      g.position.set(...position);
      g.rotation.y = rotY;
      g.userData.instanceIndex = instanceIndex;   // 0 for tables; 0..qty-1 for chairs
      s.scene.add(g);
      placedGroups.push(g);
    });
    s.itemGroups = placedGroups;

    // Auto-fit camera radius ONCE, on the first time we render items.
    // Subsequent items changes (qty bump, drag commit, AI patch) must not
    // jerk the camera back — the user has placed it where they want.
    if (!s.cameraFitted && placedGroups.length > 0) {
      const box = new THREE.Box3();
      placedGroups.forEach(g => box.expandByObject(g));
      const size = new THREE.Vector3(); box.getSize(size);
      const span = Math.max(size.x, size.z) * 1.3 + 1500;
      if (s.setRadius) s.setRadius(Math.max(2500, Math.min(15000, span)));
      s.cameraFitted = true;
    }
  }, [items]);

  // Refresh selection highlight when (selectedItemId, selectedInstanceIndex) changes.
  useEffect(() => {
    const s = stateRef.current;
    if (!s) return;
    if (s.selectionHelper) {
      s.scene.remove(s.selectionHelper);
      s.selectionHelper.geometry?.dispose?.();
      s.selectionHelper.material?.dispose?.();
      s.selectionHelper = null;
    }
    // Highlight the SPECIFIC placement matching (itemId, instanceIndex).
    // Fall back to the first placement of that itemId for clicks coming
    // from the left-panel list (which only sets itemId).
    let target = s.itemGroups?.find(g =>
      g.userData.itemId === selectedItemId &&
      g.userData.instanceIndex === selectedInstanceIndex
    );
    if (!target) target = s.itemGroups?.find(g => g.userData.itemId === selectedItemId);
    if (target) {
      const helper = new THREE.BoxHelper(target, 0xb25a2a);
      helper.material.linewidth = 2;
      s.scene.add(helper);
      s.selectionHelper = helper;
    }
  }, [selectedItemId, selectedInstanceIndex, items]);

  return <div ref={mountRef} className="cfg-3d-mount" />;
}

// ─── ViewSpec — BOM ────────────────────────────────────────────────────────

function ViewSpec({ items }) {
  const total = items.reduce((sum, it) => {
    const p = itemUnitPrice(it);
    return sum + (p == null ? 0 : p * (it.qty || 1));
  }, 0);
  return (
    <div className="cfg-spec">
      <table className="cfg-spec-table">
        <thead>
          <tr><th>#</th><th>Kind</th><th>SKU</th><th>Description</th><th>Qty</th><th>Unit (EUR)</th><th>Line (EUR)</th></tr>
        </thead>
        <tbody>
          {items.map((it, i) => {
            const price = itemUnitPrice(it);
            const line = price == null ? null : price * (it.qty || 1);
            return (
              <tr key={it.id}>
                <td>{i + 1}</td>
                <td>{it.kind}</td>
                <td className="cfg-mono">{itemSku(it)}</td>
                <td>{itemLabel(it)} <span className="cfg-muted">· {describeItem(it)}</span></td>
                <td>{it.qty}</td>
                <td>{fmtEUR(price)}</td>
                <td><strong>{fmtEUR(line)}</strong></td>
              </tr>
            );
          })}
          {!items.length && <tr><td colSpan={7} className="cfg-empty">No items.</td></tr>}
        </tbody>
        {items.length > 0 && (
          <tfoot>
            <tr><td colSpan={6} className="cfg-spec-total-label">Project total</td><td><strong>{fmtEUR(total)}</strong></td></tr>
          </tfoot>
        )}
      </table>
    </div>
  );
}

function describeItem(it) {
  if (it.kind === 'table') {
    let s = `base ${it.baseCode}, top ${it.topCode}`;
    if (it.insertOrProfile) s += `, +${it.insertOrProfile}`;
    return s;
  }
  if (it.kind === 'chair') return `frame ${it.frameCode}, uphol ${it.upholsteryCode}`;
  return '';
}

// ═══════════════════════════════════════════════════════════════════════════
//  RIGHT PANEL
// ═══════════════════════════════════════════════════════════════════════════

function RightPanel({ tab, setTab, items, selectedItem, selectedItemId, total, project, setProject, lang, ai }) {
  return (
    <aside className="cfg-right">
      <div className="cfg-right-tabs">
        {['spec', 'copilot', 'project'].map(k => (
          <button key={k} className={`cfg-tab ${tab === k ? 'active' : ''}`} onClick={() => setTab(k)}>
            {t('tab.' + k, lang)}
          </button>
        ))}
      </div>
      <div className="cfg-right-body">
        {tab === 'spec'    && <SpecPanel item={selectedItem} lang={lang} />}
        {tab === 'copilot' && (
          <AICopilot
            project={project} items={items} selectedItemId={selectedItemId}
            total={total} lang={lang} fmtEUR={fmtEUR}
            {...ai}
          />
        )}
        {tab === 'project' && <ProjectPanel project={project} setProject={setProject} total={total} items={items} lang={lang} />}
      </div>
    </aside>
  );
}

function SpecPanel({ item, lang }) {
  if (!item) return <div className="cfg-empty">{t('empty.select_item', lang)}</div>;
  const price = itemUnitPrice(item);
  const line = price == null ? null : price * (item.qty || 1);
  const warnings = itemWarnings(item);
  return (
    <div className="cfg-spec-panel">
      <div className="cfg-kv"><span>SKU</span><span className="cfg-mono">{itemSku(item)}</span></div>
      <div className="cfg-kv"><span>{t('spec.kind', lang)}</span><span>{item.kind}</span></div>
      <div className="cfg-kv"><span>{t('field.collection', lang)}</span><span>{item.collection}</span></div>
      <div className="cfg-kv"><span>{t('field.size', lang)}</span><span>{item.sizeKey}</span></div>
      {item.kind === 'table' && <>
        <div className="cfg-kv"><span>{t('field.base', lang)}</span><span>{item.baseCode} (tier {BASE_BY_CODE[item.baseCode]?.tier})</span></div>
        <div className="cfg-kv"><span>{t('field.top', lang)}</span><span>{item.topCode}</span></div>
        {item.insertOrProfile && <div className="cfg-kv"><span>Insert/Profile</span><span>{item.insertOrProfile}</span></div>}
      </>}
      {item.kind === 'chair' && <>
        <div className="cfg-kv"><span>{t('field.frame', lang)}</span><span>{item.frameCode}</span></div>
        <div className="cfg-kv"><span>{t('field.upholstery', lang)}</span><span>{item.upholsteryCode} ({chairUpholsteryGroup(item.upholsteryCode) || '—'})</span></div>
      </>}
      <div className="cfg-kv"><span>{t('spec.qty', lang)}</span><span>{item.qty}</span></div>
      <div className="cfg-kv cfg-kv-strong"><span>{t('spec.unit', lang)}</span><span>{fmtEUR(price)}</span></div>
      <div className="cfg-kv cfg-kv-strong"><span>{t('spec.line', lang)}</span><span>{fmtEUR(line)}</span></div>
      {warnings.length > 0 && (
        <div className="cfg-warnings">
          <div className="cfg-warnings-header">{t('panel.warnings', lang)}</div>
          {warnings.map((w, i) => <div key={i} className="cfg-warning">{w}</div>)}
        </div>
      )}
    </div>
  );
}

function ProjectPanel({ project, setProject, total, items }) {
  const tables = items.filter(i => i.kind === 'table').length;
  const chairs = items.reduce((s, i) => s + (i.kind === 'chair' ? i.qty : 0), 0);
  return (
    <div className="cfg-project-panel">
      {['name', 'client', 'designer', 'address', 'date', 'notes'].map(field => (
        <div key={field} className="cfg-field">
          <label>{field}</label>
          {field === 'notes'
            ? <textarea rows={4} value={project[field] || ''} onChange={e => setProject(p => ({ ...p, [field]: e.target.value }))} />
            : <input type={field === 'date' ? 'date' : 'text'} value={project[field] || ''} onChange={e => setProject(p => ({ ...p, [field]: e.target.value }))} />}
        </div>
      ))}
      <div className="cfg-field">
        <label>Status</label>
        <select value={project.status} onChange={e => setProject(p => ({ ...p, status: e.target.value }))}>
          {['draft', 'quoted', 'confirmed', 'in-production', 'delivered'].map(s => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>
      <div className="cfg-project-summary">
        <div className="cfg-kv"><span>Tables</span><span>{tables}</span></div>
        <div className="cfg-kv"><span>Chairs</span><span>{chairs}</span></div>
        <div className="cfg-kv cfg-kv-strong"><span>Total</span><span>{fmtEUR(total)}</span></div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
//  PRINT LAYOUT — hidden on screen, visible only when printing
// ═══════════════════════════════════════════════════════════════════════════

function PrintLayout({ project, items, total }) {
  const tables = items.filter(i => i.kind === 'table').length;
  const chairs = items.reduce((s, i) => s + (i.kind === 'chair' ? i.qty : 0), 0);
  return (
    <div className="cfg-print-only">
      <header className="cfg-print-header">
        <div>
          <div className="cfg-print-brand">CATTELAN ITALIA</div>
          <div className="cfg-print-subtitle">SKORPIO + BELINDA · Project specification</div>
        </div>
        <div className={`cfg-print-status cfg-status-${project.status}`}>{project.status}</div>
      </header>
      <h1>{project.name || 'Untitled project'}</h1>
      <div className="cfg-print-meta">
        <div><strong>Client:</strong> {project.client || '—'}</div>
        <div><strong>Designer:</strong> {project.designer || '—'}</div>
        <div><strong>Address:</strong> {project.address || '—'}</div>
        <div><strong>Date:</strong> {project.date || '—'}</div>
      </div>
      {project.notes && (
        <div className="cfg-print-notes">
          <strong>Notes:</strong> {project.notes}
        </div>
      )}
      <table className="cfg-print-table">
        <thead>
          <tr>
            <th>#</th><th>Kind</th><th>SKU</th><th>Description</th>
            <th>Qty</th><th>Unit (EUR)</th><th>Line (EUR)</th>
          </tr>
        </thead>
        <tbody>
          {items.map((it, i) => {
            const unit = itemUnitPrice(it);
            const line = unit == null ? null : unit * (it.qty || 1);
            return (
              <tr key={it.id}>
                <td>{i + 1}</td>
                <td>{it.kind}</td>
                <td className="cfg-mono">{itemSku(it)}</td>
                <td>{itemLabel(it)}<br/><span className="cfg-print-muted">{describeItem(it)}</span></td>
                <td>{it.qty}</td>
                <td>{fmtEUR(unit)}</td>
                <td><strong>{fmtEUR(line)}</strong></td>
              </tr>
            );
          })}
        </tbody>
        <tfoot>
          <tr>
            <td colSpan={6}>{tables} table{tables === 1 ? '' : 's'}, {chairs} chair{chairs === 1 ? '' : 's'} — total</td>
            <td><strong>{fmtEUR(total)}</strong></td>
          </tr>
        </tfoot>
      </table>
      <footer className="cfg-print-footer">
        <div>Cattelan Italia spa · via Pilastri 19 · 36010 Carrè (VI) · Italy</div>
        <div>+39 0445 318711 · info@cattelanitalia.com · cattelanitalia.com</div>
      </footer>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
//  STYLES
// ═══════════════════════════════════════════════════════════════════════════

function ConfiguratorStyles() {
  return (
    <style>{`
      :root {
        --bg:        #f3eee4;
        --bg-warm:   #ebe4d4;
        --cream:     #e0d5bc;
        --ink:       #2a2620;
        --ink-soft:  #6a6358;
        --line:      #cbc1a8;
        --accent:    #b25a2a;
      }
      * { box-sizing: border-box; }
      body, html, #root { margin: 0; padding: 0; height: 100%; font-family: 'Inter', system-ui, -apple-system, sans-serif; color: var(--ink); background: var(--bg); }
      .cfg-app { display: flex; flex-direction: column; height: 100vh; }
      .cfg-main { display: grid; grid-template-columns: 360px 1fr 360px; flex: 1; min-height: 0; }
      .cfg-left, .cfg-right { overflow-y: auto; background: var(--bg); border-right: 1px solid var(--line); }
      .cfg-right { border-right: none; border-left: 1px solid var(--line); }
      .cfg-center { display: flex; flex-direction: column; min-width: 0; background: var(--bg-warm); }

      .cfg-header { display: flex; align-items: center; gap: 16px; padding: 10px 16px; background: #fff; border-bottom: 1px solid var(--line); }
      .cfg-brand { display: flex; flex-direction: column; line-height: 1; }
      .cfg-brand-mark { font-weight: 800; letter-spacing: 0.05em; font-size: 14px; }
      .cfg-brand-sub  { font-size: 10px; color: var(--ink-soft); letter-spacing: 0.04em; text-transform: uppercase; }
      .cfg-project-strip { display: flex; gap: 8px; align-items: center; flex: 1; }
      .cfg-input { padding: 6px 10px; border: 1px solid var(--line); border-radius: 4px; background: #fff; font-size: 13px; }
      .cfg-input-name { font-weight: 600; min-width: 200px; }
      .cfg-status { padding: 3px 10px; border-radius: 12px; background: var(--cream); color: var(--accent); font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.05em; }
      .cfg-header-actions { display: flex; gap: 8px; align-items: center; }
      .cfg-total { font-size: 13px; color: var(--ink-soft); }
      .cfg-total strong { color: var(--accent); font-size: 15px; }
      .cfg-btn { display: inline-flex; align-items: center; gap: 6px; padding: 6px 12px; border: 1px solid var(--line); background: #fff; cursor: pointer; border-radius: 4px; font-size: 12px; color: var(--ink); }
      .cfg-btn:hover { background: var(--bg-warm); }
      .cfg-btn:disabled { opacity: 0.5; cursor: not-allowed; }
      .cfg-btn-add { width: 100%; justify-content: center; padding: 8px; font-size: 12px; font-weight: 600; }

      .cfg-section { padding: 12px; border-bottom: 1px solid var(--line); }
      .cfg-section-header { font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.06em; color: var(--ink-soft); margin-bottom: 8px; }

      .cfg-items-list { display: flex; flex-direction: column; gap: 4px; margin-bottom: 8px; }
      .cfg-item-row { display: grid; grid-template-columns: 18px 1fr 50px auto 24px; gap: 8px; align-items: center; padding: 8px 10px; background: #fff; border: 1px solid var(--line); border-radius: 4px; cursor: pointer; font-size: 12px; }
      .cfg-item-row:hover { background: var(--bg-warm); }
      .cfg-item-row.active { background: var(--cream); border-color: var(--accent); box-shadow: inset 3px 0 0 var(--accent); }
      .cfg-item-icon { color: var(--ink-soft); }
      .cfg-item-meta { min-width: 0; }
      .cfg-item-label { font-weight: 600; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
      .cfg-item-sku { font-family: ui-monospace, 'SF Mono', Menlo, monospace; font-size: 10px; color: var(--ink-soft); }
      .cfg-item-qty { width: 44px; padding: 2px 4px; font-size: 12px; text-align: center; border: 1px solid var(--line); border-radius: 3px; background: #fff; }
      .cfg-item-price { font-weight: 600; font-size: 12px; text-align: right; }
      .cfg-icon-btn { background: transparent; border: none; cursor: pointer; color: var(--ink-soft); padding: 2px; }
      .cfg-icon-btn:hover { color: var(--accent); }
      .cfg-add-buttons { display: grid; grid-template-columns: 1fr 1fr; gap: 6px; }

      .cfg-editor { display: flex; flex-direction: column; gap: 10px; }
      .cfg-field { display: flex; flex-direction: column; gap: 4px; }
      .cfg-field > label { font-size: 11px; font-weight: 600; color: var(--ink-soft); text-transform: uppercase; letter-spacing: 0.04em; }
      .cfg-field select, .cfg-field input, .cfg-field textarea { padding: 6px 8px; border: 1px solid var(--line); border-radius: 3px; background: #fff; font-size: 12px; font-family: inherit; }

      .cfg-toggle-group { display: flex; gap: 4px; }
      .cfg-toggle { flex: 1; padding: 6px; border: 1px solid var(--line); background: #fff; cursor: pointer; border-radius: 3px; font-size: 12px; }
      .cfg-toggle.active { background: var(--cream); border-color: var(--accent); color: var(--accent); font-weight: 600; }

      .cfg-swatch-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(56px, 1fr)); gap: 6px; }
      .cfg-swatch { cursor: pointer; border: 2px solid transparent; border-radius: 4px; padding: 2px; }
      .cfg-swatch.active { border-color: var(--accent); }
      .cfg-swatch img, .cfg-swatch-color { width: 100%; aspect-ratio: 1; border-radius: 3px; display: block; object-fit: cover; }
      .cfg-swatch-caption { font-family: ui-monospace, monospace; font-size: 10px; color: var(--ink-soft); display: block; text-align: center; margin-top: 2px; }

      .cfg-uphol-filter { display: flex; gap: 4px; margin-bottom: 6px; }
      .cfg-uphol-filter select { flex: 1; }
      .cfg-search { display: flex; align-items: center; gap: 4px; flex: 1; padding: 0 6px; border: 1px solid var(--line); border-radius: 3px; background: #fff; }
      .cfg-search input { border: none; outline: none; padding: 5px 0; flex: 1; font-size: 12px; background: transparent; }
      .cfg-uphol-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 5px; max-height: 320px; overflow-y: auto; padding: 2px; }
      .cfg-uphol-cell { cursor: pointer; border: 2px solid transparent; border-radius: 4px; padding: 2px; }
      .cfg-uphol-cell.active { border-color: var(--accent); }
      .cfg-uphol-cell img { width: 100%; aspect-ratio: 1; border-radius: 3px; display: block; object-fit: cover; }
      .cfg-uphol-placeholder { width: 100%; aspect-ratio: 1; background: repeating-linear-gradient(45deg, #ddd 0 6px, #eee 6px 12px); display: flex; align-items: center; justify-content: center; font-size: 11px; color: var(--ink-soft); }
      .cfg-uphol-caption { font-family: ui-monospace, monospace; font-size: 9px; color: var(--ink-soft); display: block; text-align: center; margin-top: 1px; }
      .cfg-uphol-more { grid-column: 1 / -1; padding: 6px; text-align: center; font-size: 11px; color: var(--ink-soft); font-style: italic; }

      .cfg-view-tabs { display: flex; gap: 2px; padding: 8px 12px 0; background: var(--bg-warm); border-bottom: 1px solid var(--line); }
      .cfg-tab { padding: 8px 14px; border: none; background: transparent; cursor: pointer; font-size: 12px; color: var(--ink-soft); border-bottom: 2px solid transparent; }
      .cfg-tab.active { color: var(--accent); border-bottom-color: var(--accent); font-weight: 600; }
      .cfg-tab:disabled { opacity: 0.4; cursor: not-allowed; }
      .cfg-view-body { flex: 1; min-height: 0; display: flex; }
      .cfg-3d-mount { flex: 1; cursor: grab; }
      .cfg-3d-mount:active { cursor: grabbing; }
      .cfg-placeholder { flex: 1; display: flex; align-items: center; justify-content: center; color: var(--ink-soft); font-style: italic; }

      .cfg-spec { flex: 1; overflow: auto; padding: 16px; }
      .cfg-spec-table { width: 100%; border-collapse: collapse; font-size: 12px; background: #fff; border: 1px solid var(--line); }
      .cfg-spec-table th, .cfg-spec-table td { padding: 6px 10px; border-bottom: 1px solid var(--line); text-align: left; }
      .cfg-spec-table th { background: var(--bg-warm); font-weight: 700; font-size: 11px; text-transform: uppercase; letter-spacing: 0.04em; }
      .cfg-spec-table tfoot td { background: var(--cream); }
      .cfg-spec-total-label { text-align: right; font-weight: 600; }
      .cfg-mono { font-family: ui-monospace, 'SF Mono', Menlo, monospace; font-size: 11px; }
      .cfg-muted { color: var(--ink-soft); font-size: 11px; }

      .cfg-right-tabs { display: flex; padding: 8px 12px 0; background: var(--bg); border-bottom: 1px solid var(--line); }
      .cfg-right-body { padding: 12px; }
      .cfg-spec-panel, .cfg-project-panel { display: flex; flex-direction: column; gap: 6px; }
      .cfg-kv { display: flex; justify-content: space-between; padding: 4px 0; border-bottom: 1px dashed var(--line); font-size: 12px; }
      .cfg-kv:last-child { border-bottom: none; }
      .cfg-kv > span:first-child { color: var(--ink-soft); }
      .cfg-kv-strong { font-weight: 600; }
      .cfg-kv-strong > span:last-child { color: var(--accent); }
      .cfg-project-summary { margin-top: 12px; padding-top: 12px; border-top: 1px solid var(--line); }
      .cfg-empty { padding: 12px; text-align: center; color: var(--ink-soft); font-style: italic; font-size: 12px; }

      .cfg-lang-picker { padding: 5px 6px; border: 1px solid var(--line); background: #fff; font-size: 11px; font-weight: 600; border-radius: 4px; cursor: pointer; }

      /* ── COPILOT ───────────────────────────────────────────────────── */
      .cfg-copilot { display: flex; flex-direction: column; height: 100%; min-height: 420px; margin: -12px; }
      .cfg-copilot-warning { padding: 8px 12px; background: #fef3e2; color: #8a4a1f; font-size: 11px; border-bottom: 1px solid var(--line); }
      .cfg-copilot-messages { flex: 1; overflow-y: auto; padding: 12px; display: flex; flex-direction: column; gap: 8px; min-height: 240px; }
      .cfg-copilot-msg { display: flex; }
      .cfg-copilot-user { justify-content: flex-end; }
      .cfg-copilot-assistant, .cfg-copilot-tool { justify-content: flex-start; }
      .cfg-copilot-bubble { max-width: 88%; padding: 8px 12px; border-radius: 12px; font-size: 12.5px; line-height: 1.45; white-space: pre-wrap; word-wrap: break-word; }
      .cfg-copilot-user .cfg-copilot-bubble { background: var(--accent); color: #fff; }
      .cfg-copilot-assistant .cfg-copilot-bubble { background: var(--bg-warm); color: var(--ink); }
      .cfg-copilot-tool .cfg-copilot-bubble { background: transparent; color: var(--ink-soft); font-family: ui-monospace, monospace; font-size: 10.5px; padding: 2px 0; max-width: 100%; }
      .cfg-copilot-thinking { font-style: italic; opacity: 0.65; }
      .cfg-copilot-error { padding: 8px 10px; background: #fde9e0; color: #8a2f1f; font-size: 11px; border-radius: 4px; }
      .cfg-copilot-input { display: flex; gap: 6px; padding: 10px; border-top: 1px solid var(--line); align-items: stretch; }
      .cfg-copilot-input textarea { flex: 1; padding: 6px 8px; border: 1px solid var(--line); border-radius: 4px; font-size: 12px; font-family: inherit; resize: none; outline: none; }
      .cfg-copilot-input textarea:focus { border-color: var(--accent); }
      .cfg-copilot-input .cfg-btn { padding: 8px 12px; }

      /* ── AI Render (button + modal) ────────────────────────────────── */
      .cfg-btn-ai { background: linear-gradient(135deg, #f5d4a8 0%, #e8b87c 100%); border-color: #d9a460; color: #5a3d1a; font-weight: 600; }
      .cfg-btn-ai:hover:not(:disabled) { background: linear-gradient(135deg, #f7dcb6 0%, #ecbe85 100%); }
      .cfg-btn-ai:disabled { opacity: 0.5; cursor: not-allowed; }

      .cfg-render-backdrop { position: fixed; inset: 0; background: rgba(0, 0, 0, 0.55); display: flex; align-items: center; justify-content: center; z-index: 1000; padding: 24px; }
      .cfg-render-modal { background: #fff; border-radius: 8px; max-width: 1100px; width: 100%; max-height: 90vh; display: flex; flex-direction: column; overflow: hidden; box-shadow: 0 12px 40px rgba(0, 0, 0, 0.3); }
      .cfg-render-header { display: flex; align-items: center; justify-content: space-between; padding: 12px 16px; border-bottom: 1px solid var(--line); font-weight: 600; }
      .cfg-render-x { background: none; border: none; cursor: pointer; padding: 4px; color: var(--ink-soft); display: flex; }
      .cfg-render-x:hover { color: var(--ink); }
      .cfg-render-body { flex: 1; min-height: 320px; display: flex; align-items: center; justify-content: center; padding: 20px; background: var(--bg-warm); overflow: auto; }
      .cfg-render-pending { display: flex; flex-direction: column; align-items: center; gap: 12px; color: var(--ink-soft); font-size: 13px; }
      .cfg-render-hint { font-size: 11px; opacity: 0.7; }
      .cfg-spinner { width: 32px; height: 32px; border: 3px solid var(--line); border-top-color: var(--accent); border-radius: 50%; animation: cfg-spin 0.9s linear infinite; }
      @keyframes cfg-spin { to { transform: rotate(360deg); } }
      .cfg-render-image { max-width: 100%; max-height: 70vh; border-radius: 4px; box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15); }
      .cfg-render-error { display: flex; flex-direction: column; align-items: center; gap: 8px; color: #8a2f1f; }
      .cfg-render-error-detail { font-size: 11px; font-family: ui-monospace, monospace; background: #fde9e0; padding: 6px 10px; border-radius: 4px; max-width: 600px; word-break: break-word; }
      .cfg-render-footer { display: flex; align-items: center; gap: 8px; padding: 10px 16px; border-top: 1px solid var(--line); justify-content: flex-end; }
      .cfg-render-model { font-size: 10px; color: var(--ink-soft); margin-right: auto; font-family: ui-monospace, monospace; }
      .cfg-btn-primary { background: var(--accent); color: #fff; border-color: var(--accent); }
      .cfg-btn-primary:hover:not(:disabled) { opacity: 0.9; }

      /* ── WARNINGS (Phase 7) ────────────────────────────────────────── */
      .cfg-warnings { margin-top: 12px; padding-top: 10px; border-top: 1px dashed var(--line); }
      .cfg-warnings-header { font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.05em; color: var(--accent); margin-bottom: 4px; }
      .cfg-warning { font-size: 11px; color: #6a4a2f; padding: 3px 0; line-height: 1.4; font-style: italic; }

      @media (max-width: 1100px) {
        .cfg-main { grid-template-columns: 320px 1fr 320px; }
      }

      /* ── PRINT LAYOUT (hidden on screen, visible only when printing) ── */
      .cfg-print-only { display: none; }

      @media print {
        body { background: #fff; }
        .cfg-app > header, .cfg-main { display: none !important; }
        .cfg-print-only { display: block !important; padding: 16mm 14mm; font-size: 10pt; color: #111; font-family: 'Inter', system-ui, sans-serif; }
        .cfg-print-header { display: flex; justify-content: space-between; align-items: flex-start; border-bottom: 0.5pt solid #222; padding-bottom: 6pt; margin-bottom: 14pt; }
        .cfg-print-brand { font-weight: 800; font-size: 11pt; letter-spacing: 0.05em; }
        .cfg-print-subtitle { font-size: 8pt; color: #555; letter-spacing: 0.04em; text-transform: uppercase; }
        .cfg-print-status { padding: 2pt 8pt; border: 0.5pt solid #b25a2a; color: #b25a2a; border-radius: 12pt; font-size: 8pt; text-transform: uppercase; letter-spacing: 0.05em; font-weight: 600; }
        .cfg-print-only h1 { font-size: 18pt; margin: 0 0 8pt; }
        .cfg-print-meta { display: grid; grid-template-columns: 1fr 1fr; gap: 4pt 24pt; margin-bottom: 14pt; font-size: 9.5pt; }
        .cfg-print-meta strong { display: inline-block; min-width: 60pt; color: #555; font-weight: 600; }
        .cfg-print-notes { padding: 8pt 10pt; background: #f8f4ed; border-left: 2pt solid #b25a2a; margin-bottom: 14pt; font-size: 9pt; }
        .cfg-print-notes strong { color: #555; }
        .cfg-print-table { width: 100%; border-collapse: collapse; margin-top: 8pt; font-size: 8.5pt; }
        .cfg-print-table th, .cfg-print-table td { padding: 5pt 7pt; border-bottom: 0.25pt solid #888; text-align: left; vertical-align: top; }
        .cfg-print-table thead th { border-bottom: 0.75pt solid #111; background: #f2efe7; font-weight: 700; text-transform: uppercase; font-size: 7.5pt; letter-spacing: 0.04em; }
        .cfg-print-table tfoot td { border-top: 0.75pt solid #111; font-weight: 700; padding-top: 7pt; background: #f2efe7; }
        .cfg-print-muted { color: #777; font-size: 7.5pt; }
        .cfg-print-footer { margin-top: 18pt; padding-top: 6pt; border-top: 0.25pt solid #888; font-size: 7pt; color: #666; text-align: center; line-height: 1.5; }
        @page { size: A4 portrait; margin: 12mm; }
      }
    `}</style>
  );
}
